import React, { useEffect, useState } from "react";
import { api, ApiError, type Subscription } from "../../api";
import { useAuth } from "../../AuthContext";
import { useTheme } from "../../ThemeContext";
import { useToast } from "../../ToastContext";
import { useLanguage, LANGUAGES } from "../../i18n";
import { NotificationsMenuItem } from "../shared/NotificationSettings";
import { RateAppMenuItem } from "../shared/AppPrompts";

type Panel = "appearance" | "language" | "terms" | "bank" | null;

const SUPPORT_EMAIL = "support@toletbook.in";

function icon(paths: React.ReactNode) {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
      {paths}
    </svg>
  );
}
const REPORTS_ICON = icon(<><line x1="6" y1="20" x2="6" y2="16" /><line x1="12" y1="20" x2="12" y2="10" /><line x1="18" y1="20" x2="18" y2="4" /></>);
const GIFT_ICON = icon(<><rect x="3" y="8" width="18" height="4" rx="1" /><path d="M12 8v13" /><path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7" /><path d="M7.5 8a2.5 2.5 0 0 1 0-5C11 3 12 8 12 8s1-5 4.5-5a2.5 2.5 0 0 1 0 5" /></>);
const BROADCAST_ICON = icon(<path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />);
const MOON_ICON = icon(<path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />);
const GLOBE_ICON = icon(<><circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></>);
const BANK_ICON = icon(<><line x1="3" y1="21" x2="21" y2="21" /><line x1="5" y1="21" x2="5" y2="10" /><line x1="19" y1="21" x2="19" y2="10" /><polygon points="12 3 21 9 3 9" /></>);
const FILE_ICON = icon(<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="8" y1="13" x2="16" y2="13" /><line x1="8" y1="17" x2="16" y2="17" /></>);
const SHIELD_ICON = icon(<path d="M12 2 4 5v6c0 5.5 3.8 9 8 11 4.2-2 8-5.5 8-11V5z" />);
const MAIL_ICON = icon(<><rect x="2" y="4" width="20" height="16" rx="2" /><polyline points="2 6 12 13 22 6" /></>);
const LOGOUT_ICON = icon(<><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></>);
const TRASH_ICON = icon(<><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><line x1="10" y1="11" x2="10" y2="17" /><line x1="14" y1="11" x2="14" y2="17" /></>);

function fmtDate(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

export function OwnerSettings({ onNavigate }: { onNavigate?: (view: "reports" | "plans" | "refer" | "broadcast") => void }) {
  const { user, logout, updateUser } = useAuth();
  const { theme, setTheme } = useTheme();
  const { lang, setLang, t } = useLanguage();
  const toast = useToast();
  const [open, setOpen] = useState<Panel>(null);
  const [sub, setSub] = useState<Subscription | null>(null);
  const [payoutMethod, setPayoutMethod] = useState<"upi" | "bank">(user?.bankAccountNumber ? "bank" : "upi");
  const [upiId, setUpiId] = useState(user?.upiId ?? "");
  const [bankAccountHolder, setBankAccountHolder] = useState(user?.bankAccountHolder ?? "");
  const [bankAccountNumber, setBankAccountNumber] = useState(user?.bankAccountNumber ?? "");
  const [bankIfsc, setBankIfsc] = useState(user?.bankIfsc ?? "");
  const [bankBusy, setBankBusy] = useState(false);
  const [bankError, setBankError] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);

  useEffect(() => {
    api.get("/api/subscription").then(setSub).catch(() => {});
  }, []);

  function toggle(p: Exclude<Panel, null>) {
    setOpen((cur) => (cur === p ? null : p));
  }

  async function requestDeletion() {
    if (!confirm(t("settings.confirmDeleteAccount"))) return;
    setDeleteBusy(true);
    try {
      await api.post("/api/auth/request-deletion");
      updateUser({ deletionRequestedAt: new Date().toISOString() });
      toast(t("settings.deletionRequestedToast"));
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t("common.somethingWrong"));
    } finally {
      setDeleteBusy(false);
    }
  }

  async function saveBankDetails() {
    setBankBusy(true);
    setBankError("");
    try {
      const { user: updated } = await api.patch("/api/auth/me", {
        upiId: upiId.trim() || null,
        bankAccountHolder: bankAccountHolder.trim() || null,
        bankAccountNumber: bankAccountNumber.trim() || null,
        bankIfsc: bankIfsc.trim() || null,
      });
      updateUser(updated);
      toast(t("settings.bankDetailsSaved"));
    } catch (err) {
      setBankError(err instanceof ApiError ? err.message : t("common.somethingWrong"));
    } finally {
      setBankBusy(false);
    }
  }

  const currentLangLabel = LANGUAGES.find((l) => l.code === lang)?.label ?? "English";
  const planName = sub ? t(`ownerPlans.${sub.plan}Name`) : "";
  const validity =
    sub?.status === "trialing"
      ? `🎁 ${t("ownerPlans.trialBadge")} · ${t("ownerPlans.trialEndsOn", { date: fmtDate(sub.trialEndsAt) })}`
      : sub?.status === "active" && sub.currentPeriodEnd
      ? t("ownerPlans.renewsOn", { date: fmtDate(sub.currentPeriodEnd) })
      : "";

  return (
    <>
      <div className="section-title">{t("settings.title")}</div>

      {sub && (
        <div className="summary-card" style={{ cursor: "pointer" }} onClick={() => onNavigate?.("plans")}>
          <div className="summary-top">
            <span>{t("ownerPlans.currentPlanLabel")}</span>
            <span>›</span>
          </div>
          <div style={{ fontFamily: "'Baloo 2',sans-serif", fontSize: 26, fontWeight: 700, marginTop: 8 }}>
            {planName}
          </div>
          {validity && <div style={{ fontSize: 12.5, fontWeight: 600, opacity: 0.85, marginTop: 4 }}>{validity}</div>}
        </div>
      )}

      <div className="section-eyebrow">{t("settings.generalSection")}</div>
      <div className="menu-group">
        <div className="menu-item" onClick={() => onNavigate?.("reports")}>
          <div className="menu-icon purple">{REPORTS_ICON}</div>
          <span className="mi-label">{t("settings.reportsMenu")}</span> <span className="mi-right">›</span>
        </div>

        <div className="menu-item" onClick={() => onNavigate?.("refer")}>
          <div className="menu-icon gold">{GIFT_ICON}</div>
          <span className="mi-label">{t("settings.referMenu")}</span> <span className="mi-right">›</span>
        </div>

        <div className="menu-item" onClick={() => onNavigate?.("broadcast")}>
          <div className="menu-icon green">{BROADCAST_ICON}</div>
          <span className="mi-label">{t("settings.broadcastMenu")}</span> <span className="mi-right">›</span>
        </div>

        <NotificationsMenuItem />
        <RateAppMenuItem />

        <div className="menu-item" onClick={() => toggle("appearance")}>
          <div className="menu-icon purple">{MOON_ICON}</div>
          <span className="mi-label">{t("settings.appearance")}</span>{" "}
          <span className="mi-right">{theme === "dark" ? `🌙 ${t("settings.dark")} ›` : `☀️ ${t("settings.light")} ›`}</span>
        </div>
        {open === "appearance" && (
          <div style={{ padding: "14px 14px 18px" }}>
            <div className="chip-row" style={{ marginBottom: 0 }}>
              <div className={`chip${theme === "dark" ? " selected" : ""}`} onClick={() => setTheme("dark")}>
                🌙 {t("settings.dark")}
              </div>
              <div className={`chip${theme === "light" ? " selected" : ""}`} onClick={() => setTheme("light")}>
                ☀️ {t("settings.light")}
              </div>
            </div>
          </div>
        )}

        <div className="menu-item" onClick={() => toggle("language")}>
          <div className="menu-icon blue">{GLOBE_ICON}</div>
          <span className="mi-label">{t("settings.language")}</span> <span className="mi-right">{currentLangLabel} ›</span>
        </div>
        {open === "language" && (
          <div style={{ padding: "14px 14px 18px" }}>
            <div className="chip-row" style={{ marginBottom: 0, flexWrap: "wrap" }}>
              {LANGUAGES.map((l) => (
                <div
                  key={l.code}
                  className={`chip${lang === l.code ? " selected" : ""}`}
                  onClick={() => setLang(l.code)}
                >
                  {l.label}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="section-eyebrow">{t("settings.supportSection")}</div>
      <div className="menu-group">
        <div className="menu-item" onClick={() => toggle("bank")}>
          <div className="menu-icon green">{BANK_ICON}</div>
          <span className="mi-label">{t("settings.bankDetailsMenu")}</span> <span className="mi-right">›</span>
        </div>
        {open === "bank" && (
          <div style={{ padding: "14px 14px 18px" }}>
            <div className="field-hint" style={{ marginBottom: 10 }}>{t("settings.bankDetailsHint")}</div>
            {bankError && <div className="error-banner">{bankError}</div>}

            <div className="chip-row">
              <div className={`chip${payoutMethod === "upi" ? " selected" : ""}`} onClick={() => setPayoutMethod("upi")}>
                {t("auth.payoutMethodUpi")}
              </div>
              <div className={`chip${payoutMethod === "bank" ? " selected" : ""}`} onClick={() => setPayoutMethod("bank")}>
                {t("auth.payoutMethodBank")}
              </div>
            </div>

            {payoutMethod === "upi" ? (
              <div className="field" style={{ marginBottom: 0 }}>
                <label>{t("auth.upiIdLabel")}</label>
                <div className="input-wrap">
                  <input value={upiId} onChange={(e) => setUpiId(e.target.value)} placeholder={t("auth.upiIdPlaceholder")} />
                </div>
              </div>
            ) : (
              <>
                <div className="field">
                  <label>{t("auth.bankAccountHolderLabel")}</label>
                  <div className="input-wrap">
                    <input value={bankAccountHolder} onChange={(e) => setBankAccountHolder(e.target.value)} placeholder={t("auth.bankAccountHolderPlaceholder")} />
                  </div>
                </div>
                <div className="field">
                  <label>{t("auth.bankAccountNumberLabel")}</label>
                  <div className="input-wrap">
                    <input
                      inputMode="numeric"
                      value={bankAccountNumber}
                      onChange={(e) => setBankAccountNumber(e.target.value.replace(/\D/g, "").slice(0, 18))}
                      placeholder={t("auth.bankAccountNumberPlaceholder")}
                    />
                  </div>
                </div>
                <div className="field" style={{ marginBottom: 0 }}>
                  <label>{t("auth.bankIfscLabel")}</label>
                  <div className="input-wrap">
                    <input value={bankIfsc} onChange={(e) => setBankIfsc(e.target.value.toUpperCase().slice(0, 11))} placeholder={t("auth.bankIfscPlaceholder")} />
                  </div>
                </div>
              </>
            )}

            <button className="btn-solid btn-sm" style={{ marginTop: 14 }} disabled={bankBusy} onClick={saveBankDetails}>
              {bankBusy ? t("common.saving") : t("common.save")}
            </button>
          </div>
        )}

        <a className="menu-item" href="/terms.html" target="_blank" rel="noopener">
          <div className="menu-icon purple">{FILE_ICON}</div>
          <span className="mi-label">{t("settings.termsTitle")}</span> <span className="mi-right">›</span>
        </a>

        <a className="menu-item" href="/privacy.html" target="_blank" rel="noopener">
          <div className="menu-icon purple">{SHIELD_ICON}</div>
          <span className="mi-label">{t("settings.privacyTitle")}</span> <span className="mi-right">›</span>
        </a>

        <a className="menu-item" href={`mailto:${SUPPORT_EMAIL}`}>
          <div className="menu-icon purple">{MAIL_ICON}</div>
          <span className="mi-label">{t("settings.contactUsMenu")}</span> <span className="mi-right">{SUPPORT_EMAIL}</span>
        </a>
      </div>

      <div className="section-eyebrow">{t("settings.accountSection")}</div>
      <div className="menu-group">
        <div className="menu-item danger" onClick={logout}>
          <div className="menu-icon red">{LOGOUT_ICON}</div>
          <span className="mi-label">{t("common.logOut")}</span>
        </div>

        {user?.deletionRequestedAt ? (
          <div className="menu-item" style={{ opacity: 0.6 }}>
            {t("settings.deletionRequestedLabel")}
          </div>
        ) : (
          <div className="menu-item danger" onClick={deleteBusy ? undefined : requestDeletion}>
            <div className="menu-icon red">{TRASH_ICON}</div>
            <span className="mi-label">{deleteBusy ? t("settings.deletingBusy") : t("settings.deleteAccountMenu")}</span>
          </div>
        )}
      </div>
    </>
  );
}
